import { Observable, Subject } from 'rxjs';
import { Injectable, OnDestroy } from '@angular/core';
import io from 'socket.io-client';
import { telemetry } from '../telemetry/telemetry';
import { environment } from 'src/environments/environment';
import { ReportJob } from './integration.service';
import { CommonCommunicationService } from './commonCommunication.service';

/**
 * Unified Socket.IO client wrapper. The admin talks to two separate Socket.IO
 * servers mounted on the API gateway:
 *
 *   - order-service:        `/socket.io`                      — room-scoped
 *                                                               orderRefresh +
 *                                                               admin_notification
 *   - integrations-service: `/integrations-service/socket.io` — user-scoped
 *                                                               reports:job-updated
 *
 * The two connections stay distinct (different paths, auth, and rooms) but
 * are managed from a single service so consumers have one place to look.
 */
@Injectable({
  providedIn: 'root'
})
export class WebSocketService implements OnDestroy {
  // ─── Order-service socket (eager, room-based) ─────────────────────
  private socket: any;
  private readonly apiBaseUrl = environment.apiBaseUrl;
  // Must match the order-service socket path (CONTEXT_PATH + '/socket.io',
  // where CONTEXT_PATH = /order-service/api) so it routes through the same
  // gateway prefix as the order REST API.
  private readonly ORDER_SOCKET_PATH = '/order-service/api/socket.io';
  private readonly REFRESH_EVENT = 'orderRefresh';
  private readonly JOIN_ROOM_EVENT = 'join_room';
  private readonly LEAVE_ROOM_EVENT = 'leave_room';
  // The shop room we should be in. Persisted (NOT one-shot) so we can re-join
  // on every reconnect — socket.io assigns a new socket with no room
  // membership after a reconnect, so without re-joining the client goes silent.
  private currentRoom: string | null = null;
  // Multicast subjects so each event handler is registered on the socket ONCE,
  // instead of once per component subscription (which stacked duplicate
  // handlers and caused duplicate events across component re-inits).
  private readonly orderUpdates$ = new Subject<any>();
  private readonly adminNotifications$ = new Subject<any>();
  private readonly reconnected$ = new Subject<void>();

  // ─── Reports socket (lazy, refCount'd) ────────────────────────────
  private reportsSocket: any = null;
  private readonly reportsUpdates$ = new Subject<ReportJob>();
  private reportsRefCount = 0;

  constructor(private readonly ccs: CommonCommunicationService) {
    this.socket = io(this.apiBaseUrl, {
      path: this.ORDER_SOCKET_PATH,
      transports: ['websocket'],
      // Read the JWT fresh on every (re)connect so the handshake always carries
      // a valid token — same source as the REST X-User-Access-Token header.
      auth: (cb: (data: { token: string }) => void) => cb({ token: this.ccs.getAuthToken() || '' }),
      autoConnect: true
    });

    // Register data-event handlers ONCE on the socket; fan out via subjects.
    this.socket.on(this.REFRESH_EVENT, (data: any) => this.orderUpdates$.next(data));
    this.socket.on('admin_notification', (data: any) => this.adminNotifications$.next(data));

    this.socket.on('connect', () => {
      telemetry.breadcrumb({ category: 'socket', level: 'info', message: 'orders.connected' });
      // Re-assert room membership on EVERY (re)connect. socket.io drops rooms
      // across reconnects, so this is what keeps a backgrounded/blipped tab —
      // and every user on the shop — receiving events after they reconnect.
      if (this.currentRoom) {
        this.socket.emit(this.JOIN_ROOM_EVENT, this.currentRoom);
      }
      // Signal consumers to refetch: events emitted while we were disconnected
      // are not buffered by socket.io and would otherwise be lost.
      this.reconnected$.next();
    });
    this.socket.on('disconnect', (reason: string) => {
      telemetry.breadcrumb({
        category: 'socket',
        level: 'warning',
        message: 'orders.disconnected',
        data: { reason }
      });
    });
    this.socket.on('connect_error', (err: Error) => {
      telemetry.captureMessage(`socket.orders.connect_error: ${err?.message ?? 'unknown'}`, {
        level: 'warning',
        tags: { socket: 'orders' }
      });
    });
    this.socket.io?.on?.('reconnect_failed', () => {
      telemetry.captureMessage('socket.orders.reconnect_failed', {
        level: 'error',
        tags: { socket: 'orders' }
      });
    });
  }

  joinRoom(shopId: string): void {
    // Remember the room so we can re-join on reconnect.
    this.currentRoom = shopId;
    if (this.socket?.connected) {
      this.socket.emit(this.JOIN_ROOM_EVENT, shopId);
    }
    // If not connected yet, the 'connect' handler joins currentRoom.
  }

  leaveRoom(shopId: string): void {
    if (this.currentRoom === shopId) {
      this.currentRoom = null;
    }
    if (this.socket?.connected) {
      this.socket.emit(this.LEAVE_ROOM_EVENT, shopId);
    }
  }

  onOrderUpdate(): Observable<any> {
    return this.orderUpdates$.asObservable();
  }

  onAdminNotification(): Observable<any> {
    return this.adminNotifications$.asObservable();
  }

  /**
   * Emits whenever the order socket (re)connects. Consumers should refetch the
   * current list/stats on this, because events emitted while the socket was
   * disconnected (e.g. a backgrounded tab) are not replayed by socket.io.
   */
  onReconnect(): Observable<void> {
    return this.reconnected$.asObservable();
  }

  /**
   * Open (or increment reference count on) the integrations-service socket
   * and return the report-job-updates stream. Call disconnectReports() when
   * the consumer is done — when the ref count hits 0 the socket closes.
   */
  connectReports(accessToken: string): Observable<ReportJob> {
    this.reportsRefCount += 1;
    if (!this.reportsSocket) {
      // Connect to the integrations ORIGIN with the server's FULL socket path.
      // The server mounts Socket.IO at CONTEXT_PATH + '/socket.io' =
      // '/integrations-service/socket.io'. Passing the '/integrations-service/api'
      // sub-path as the io() URL makes socket.io-client treat it as a namespace
      // and send the handshake to the wrong path ('/socket.io' → order-service).
      const url = environment.integrationService.replace(/\/integrations-service\/api\/?$/, '');
      this.reportsSocket = io(url, {
        path: '/integrations-service/socket.io',
        auth: { token: accessToken },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionDelay: 1_000,
        reconnectionDelayMax: 10_000
      });

      this.reportsSocket.on('reports:job-updated', (payload: { job: ReportJob }) => {
        if (payload?.job) this.reportsUpdates$.next(payload.job);
      });

      this.reportsSocket.on('connect', () => {
        telemetry.breadcrumb({ category: 'socket', level: 'info', message: 'reports.connected' });
      });
      this.reportsSocket.on('disconnect', (reason: string) => {
        telemetry.breadcrumb({
          category: 'socket',
          level: 'warning',
          message: 'reports.disconnected',
          data: { reason }
        });
      });
      this.reportsSocket.on('connect_error', (err: Error) => {
        telemetry.captureMessage(`socket.reports.connect_error: ${err?.message ?? 'unknown'}`, {
          level: 'warning',
          tags: { socket: 'reports' }
        });
      });
      this.reportsSocket.io?.on?.('reconnect_failed', () => {
        telemetry.captureMessage('socket.reports.reconnect_failed', {
          level: 'error',
          tags: { socket: 'reports' }
        });
      });
    }
    return this.reportsUpdates$.asObservable();
  }

  disconnectReports(): void {
    this.reportsRefCount = Math.max(0, this.reportsRefCount - 1);
    if (this.reportsRefCount === 0 && this.reportsSocket) {
      this.reportsSocket.disconnect();
      this.reportsSocket = null;
    }
  }

  ngOnDestroy(): void {
    if (this.socket) this.socket.disconnect();
    if (this.reportsSocket) this.reportsSocket.disconnect();
  }
}
